import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Badge } from '@/components/ui/badge';
import { Tooltip, TooltipContent, TooltipTrigger, TooltipProvider } from '@/components/ui/tooltip';
import { CheckCircle2, Clock, AlertTriangle, Ban, HelpCircle } from 'lucide-react';

type StatusKey = 'sent' | 'pending' | 'bounced' | 'failed' | 'dlq' | 'complained' | 'suppressed' | 'unknown';

const META: Record<StatusKey, { label: string; cls: string; Icon: any }> = {
  sent:       { label: 'Leveret',  cls: 'bg-emerald-500/10 text-emerald-700 border-emerald-500/20', Icon: CheckCircle2 },
  pending:    { label: 'I kø',     cls: 'bg-amber-500/10 text-amber-700 border-amber-500/20', Icon: Clock },
  bounced:    { label: 'Bouncet',  cls: 'bg-orange-500/10 text-orange-700 border-orange-500/20', Icon: AlertTriangle },
  failed:     { label: 'Fejlet',   cls: 'bg-destructive/10 text-destructive border-destructive/20', Icon: AlertTriangle },
  dlq:        { label: 'Fejlet',   cls: 'bg-destructive/10 text-destructive border-destructive/20', Icon: AlertTriangle },
  complained: { label: 'Klage',    cls: 'bg-orange-500/10 text-orange-700 border-orange-500/20', Icon: AlertTriangle },
  suppressed: { label: 'Blokeret', cls: 'bg-muted text-muted-foreground border-border', Icon: Ban },
  unknown:    { label: 'Ukendt status', cls: 'bg-muted text-muted-foreground border-border', Icon: HelpCircle },
};

interface Props {
  messageId?: string | null;
  /** "dark" when rendered inside a dark (staff) bubble */
  variant?: 'light' | 'dark';
}

/** Visible delivery status for an outbound portal email, looked up via email_send_log.message_id. */
export function MessageDeliveryBadge({ messageId, variant = 'light' }: Props) {
  const { data } = useQuery({
    queryKey: ['email-delivery', messageId],
    enabled: !!messageId,
    staleTime: 15_000,
    refetchInterval: (q) => {
      const row = (q.state.data as any) || null;
      // Re-poll while pending/unknown so the badge updates as the queue runs
      if (!row || row.status === 'pending') return 10_000;
      return false;
    },
    queryFn: async () => {
      const { data } = await supabase
        .from('email_send_log' as any)
        .select('status, error_message, created_at')
        .eq('message_id', messageId!)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();
      return (data as unknown) as { status: string; error_message: string | null; created_at: string } | null;
    },
  });

  if (!messageId) return null;

  const key: StatusKey = (data?.status as StatusKey) || 'unknown';
  const meta = META[key] || META.unknown;
  const Icon = meta.Icon;
  const tone = variant === 'dark'
    ? 'bg-white/15 text-white/90 border-white/20'
    : meta.cls;

  return (
    <TooltipProvider delayDuration={150}>
      <Tooltip>
        <TooltipTrigger asChild>
          <Badge variant="outline" className={`${tone} h-4 px-1.5 text-[10px] font-medium gap-1`}>
            <Icon className="w-2.5 h-2.5" />
            {meta.label}
          </Badge>
        </TooltipTrigger>
        <TooltipContent side="top" className="max-w-xs text-xs">
          <div className="font-semibold mb-0.5">Leveringsstatus: {meta.label}</div>
          {data?.error_message && (
            <div className="text-muted-foreground break-words">{data.error_message}</div>
          )}
          {!data && <div className="text-muted-foreground">Ikke registreret i email-log</div>}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
